/* eslint-disable @typescript-eslint/consistent-type-imports */
import { Paper, Text, Group, Badge, Stack, Avatar, useMantineColorScheme, Button } from '@mantine/core';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { FaComment, FaAt, FaUserMinus, FaProjectDiagram } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { toaster } from '../../../components/ui/toaster';
import { Notification, NotificationType } from '../../../types';

import styles from './Notifications.module.css';

// Utility function to parse and format mentions in the content
const formatContent = (content: string): React.ReactNode => {
  if (!content) return null;
  return content.replace(/@\[(.*?):(.*?)\]/g, '@$2');
};

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => Promise<void>;
  onClose: () => void;
}

// Define the action interface
interface NotificationAction {
  label?: string;
  icon?: React.ReactNode;
  color?: string;
  variant?: 'filled' | 'outline';
  onClick: (e: React.MouseEvent) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  badgeColor?: string;
  badgeLabel?: string;
}

interface NotificationDisplay {
  icon: React.ReactNode;
  iconColor: string;
  displayTitle: string;
  actions: NotificationAction[];
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead, onClose }) => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const navigate = useNavigate();

  const { type, isRead, createdAt } = notification;
  const { title, message, relatedId, mentionedUserIds } = notification.data || {};
  const isMention = mentionedUserIds && mentionedUserIds.length > 0;

  // Get formatted time ago
  const timeAgo = (() => {
    try {
      if (!createdAt) return 'recently';
      return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
    } catch {
      return 'recently';
    }
  })();

  // Centralized display generator
  const getNotificationDisplay = (): NotificationDisplay => {
    if (type === NotificationType.PROJECT_ACCESS) {
      return {
        icon: <FaUserMinus size={10} />,
        iconColor: 'orange.6',
        displayTitle: title || 'User activity notification',
        actions: [
          {
            label: 'View Project',
            variant: 'outline',
            onClick: async () => {
              navigate(`/projects/${relatedId}`);
            },
          },
        ],
      };
    }
    if (type === NotificationType.PROJECT_STATUS_CHANGE) {
      return {
        icon: <FaProjectDiagram size={10} />,
        iconColor: 'teal.6',
        displayTitle: title || 'Project Status Updated',
        actions: [
          {
            label: 'View Project',
            variant: 'outline',
            onClick: async () => {
              navigate(`/projects/${relatedId}`);
            },
          },
        ],
      };
    }
    if (type === NotificationType.COMMENT || isMention) {
      return {
        icon: isMention ? <FaAt size={10} /> : <FaComment size={10} />,
        iconColor: isMention ? 'blue.6' : 'gray.6',
        displayTitle: title || (isMention ? 'You were mentioned in a comment' : 'New comment'),
        actions: [
          {
            label: 'View Comment',
            variant: 'outline',
            onClick: async () => {
              if (notification.data?.projectId && relatedId) {
                navigate(`/projects/${notification.data.projectId}?commentId=${relatedId}`);
              } else {
                toaster.error({
                  title: 'Navigation Failed',
                  description: 'Missing project or comment information.',
                });
              }
            },
          },
        ],
      };
    }
    // Default fallback
    return {
      icon: <FaComment size={10} />,
      iconColor: 'gray.6',
      displayTitle: title || 'New comment',
      actions: [],
    };
  };

  const rawChecklistItemNumber = notification.comment?.checklistItemNumber;
  const checklistItemNumber = typeof rawChecklistItemNumber === 'number' ? rawChecklistItemNumber : undefined;
  const content = notification.comment?.content || message;
  const { icon, iconColor, displayTitle, actions } = getNotificationDisplay();

  return (
    <Paper
      className={styles.notificationPanel}
      py="xs"
      px="sm"
      radius={0}
      withBorder={false}
      style={{
        opacity: isRead ? 0.7 : 1,
        borderBottom: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
        transition: 'background-color 0.2s ease',
      }}
      data-testid="notification-item"
    >
      <Group wrap="nowrap" gap="xs" align="flex-start">
        <Avatar radius="xl" color={iconColor} size="sm">
          {icon}
        </Avatar>

        <Stack gap={4} style={{ flex: 1 }}>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Text size="xs" fw={500} style={{ flex: 1, wordBreak: 'break-word' }}>
              {displayTitle}
            </Text>

            {checklistItemNumber && (
              <Badge size="xs" variant={isDark ? 'filled' : 'light'} color="blue">
                #{checklistItemNumber}
              </Badge>
            )}
          </Group>

          {content && (
            <Text size="xs" c={isDark ? 'gray.3' : 'gray.7'} style={{ wordBreak: 'break-word' }}>
              {formatContent(content)}
            </Text>
          )}

          {actions.length > 0 && (
            <Group mt="xs">
              {actions.map((action, idx) => (
                <React.Fragment key={idx}>
                  {action.badgeLabel && (
                    <Badge color={action.badgeColor || 'gray'} mr={action.label ? 'xs' : undefined}>
                      {action.badgeLabel}
                    </Badge>
                  )}
                  {action.label && (
                    <Button
                      size="xs"
                      variant={action.variant}
                      color={action.color}
                      leftSection={action.icon}
                      onClick={async (e) => {
                        await onMarkAsRead(notification.id);
                        await action.onClick(e);

                        onClose();
                      }}
                      loading={action.loading}
                      disabled={action.disabled}
                    >
                      {action.label}
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </Group>
          )}

          <Text size="xs" c={isDark ? 'gray.4' : 'gray.6'} fs="italic">
            {timeAgo}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
};
